import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { DateTime } from 'luxon';
import { formatColumnDate, dateFilterParams, headerInfoTooltipDef } from '../lib/utils';

const defaultFilterParams = {
  newRowsAction: 'keep',
};

export default [
  {
    cellClass: 'ticker-cell',
    cellRenderer: 'tickerWidgetCell',
    field: 'ticker',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 80,
    minWidth: 70,
    ...headerInfoTooltipDef('Symbol associated with the company who is having the conference call'),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: ({ data }: any) => {
      return (
        <div className="company-name-cell">
          <span>{data.name}</span>
          {data.logo && <img alt="" className="bz-ag-table__company-image" loading="lazy" src={data.logo}></img>}
        </div>
      );
    },
    field: 'name',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Company Name',
    initialWidth: 200,
    minWidth: 180,
    ...headerInfoTooltipDef('Full name of company associated to ticker'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'date',
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },
    headerName: 'Call Date',
    initialSort: 'asc' as ColDef['initialSort'],
    initialWidth: 100,
    minWidth: 90,
    valueFormatter: ({ data }) => formatColumnDate(data.date),
    valueGetter: ({ data }) => {
      if (!data.date) return null;

      return data.date;
    },
    ...headerInfoTooltipDef('Date of Call'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'start_time',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      values: [
        {
          label: 'AM',
          rules: (value: number) => {
            return DateTime.fromJSDate(new Date(value)).hour < 13;
          },
        },
        {
          label: 'PM',
          rules: (value: number) => {
            return DateTime.fromJSDate(new Date(value)).hour >= 13;
          },
        },
      ],
    },
    headerName: 'Start Time',
    initialWidth: 100,
    minWidth: 90,
    valueFormatter: ({ value }) => {
      if (!value) return '';

      return DateTime.fromJSDate(value).toFormat('h:mm a');
    },
    valueGetter: ({ data }) => {
      if (!data.start_time) return '';

      return DateTime.fromFormat(data.start_time, 'HH:mm:ss').toJSDate();
    },
    ...headerInfoTooltipDef('Time of Call'),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: ({ data }: { data: any }) => {
      if (!data.webcast_url) return '';
      return (
        <a href={data.webcast_url} rel="noreferrer" target="_blank">
          {data.webcast_url}
        </a>
      );
    },
    field: 'webcast_url',
    headerName: 'Webcast',
    initialWidth: 180,
    maxWidth: 200,
    suppressHeaderMenuButton: true,
    ...headerInfoTooltipDef('URL For Joining the Call’s Webcast'),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: ({ data }: { data: any }) => {
      if (!data.phone_num) return '-';
      return (
        <a href={`tel:${data.phone_num}`} rel="noreferrer" target="_blank">
          {data.phone_num}
        </a>
      );
    },
    field: 'phone_num',
    headerName: 'Dial In',
    initialWidth: 100,
    minWidth: 100,
    suppressHeaderMenuButton: true,
    ...headerInfoTooltipDef('Dial in number for participants outside of the US'),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: ({ data }: { data: any }) => {
      if (!data.access_code) return '-';
      return data.access_code;
    },
    field: 'access_code',
    headerName: 'Access Code',
    initialWidth: 110,
    minWidth: 100,
    suppressHeaderMenuButton: true,
    ...headerInfoTooltipDef('Code participants must type in to access call'),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: () => {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a href="https://www.benzinga.com/profile/portfolio/?action=login&utm_source=calendars" target="_blank">
          Get Alert
        </a>
      );
    },
    field: 'get_alert',
    headerName: 'Get Alert',
    initialWidth: 90,
    minWidth: 85,
    suppressAutoSize: true,
    suppressHeaderMenuButton: true,
    suppressSizeToFit: true,
  },
] as (ColDef | ColGroupDef)[];
